import { fileUrl } from "@/constants/roles";

// Renders manual section blocks. Inline markup inside text: **bold**, *italic*, [label](https://url).
const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*\n]+\*|\[[^\]]+\]\((?:https?:\/\/|\/)[^)\s]+\))/g;

export const renderInline = (text) => {
  const parts = [];
  let last = 0;
  let key = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={key++} className="font-bold text-[var(--pv-text)]">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("[")) {
      const mm = tok.match(/\[([^\]]+)\]\(([^)\s]+)\)/);
      parts.push(
        <a key={key++} href={mm[2]} target="_blank" rel="noreferrer" className="font-semibold text-cyan-400 underline decoration-cyan-500/40 underline-offset-2 transition-colors hover:text-cyan-300">
          {mm[1]}
        </a>
      );
    } else {
      parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const Block = ({ block }) => {
  switch (block.type) {
    case "heading": {
      const cls =
        block.level === 1
          ? "font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]"
          : block.level === 3
            ? "font-display text-base font-bold text-[var(--pv-text)]"
            : "font-display text-xl font-bold tracking-tight text-[var(--pv-text)]";
      return <p data-testid="manual-block-heading" className={`${cls} mt-8 first:mt-0`}>{block.text}</p>;
    }
    case "paragraph":
      return (
        <p data-testid="manual-block-paragraph" className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[var(--pv-text-2)] first:mt-0">
          {renderInline(block.text)}
        </p>
      );
    case "bullets":
      return (
        <ul data-testid="manual-block-bullets" className="mt-4 list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-[var(--pv-text-2)] marker:text-cyan-400 first:mt-0">
          {block.items.map((it, i) => (
            <li key={i}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    case "numbers":
      return (
        <ol data-testid="manual-block-numbers" className="mt-4 list-decimal space-y-1.5 pl-6 text-sm leading-relaxed text-[var(--pv-text-2)] marker:font-bold marker:text-cyan-400 first:mt-0">
          {block.items.map((it, i) => (
            <li key={i}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    case "divider":
      return <hr data-testid="manual-block-divider" className="my-8 border-[var(--pv-border)]" />;
    case "image":
      return (
        <figure data-testid="manual-block-image" className="mt-6 first:mt-0">
          <img
            src={fileUrl(block.path)}
            alt={block.alt || block.caption || "Manual image"}
            loading="lazy"
            className="w-full max-w-3xl rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] object-contain"
          />
          {block.caption && (
            <figcaption className="mt-2 text-xs font-medium italic text-[var(--pv-text-4)]">{block.caption}</figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
};

export const ManualBlockRenderer = ({ blocks }) => (
  <div data-testid="manual-block-renderer">
    {(blocks || []).map((b, i) => (
      <Block key={i} block={b} />
    ))}
  </div>
);
